import React, { useMemo, useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { getAssociates, transferAssociate } from '../../services/associate';
import { hasReadPermission, hasWritePermission } from '../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import { Avatar } from '@mui/material';
import AlertModal from '../../components/AlertModal';
import RefreshButton from '../../components/RefreshButton/RefreshButton';

const AssociateTransfer = () => {
  const canRead = hasReadPermission('associate.associate-transfer') || hasReadPermission('associate.associate-change') || hasReadPermission('associate') || hasReadPermission('associate-transfer');
  const canWrite = hasWritePermission('associate.associate-transfer') || hasWritePermission('associate.associate-change') || hasWritePermission('associate') || hasWritePermission('associate-transfer');
  const history = useHistory();
  const [transferFormData, setTransferFormData] = useState({
    transferDate: new Date().toISOString().slice(0, 16), 
    associate: null,
    toSponsor: null,
    remarks: '',
  });

  const [associates, setAssociates] = useState([]);
  const [associatesLoading, setAssociatesLoading] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [sponsorsLoading, setSponsorsLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [associateSearchTerm, setAssociateSearchTerm] = useState('');
  const [sponsorSearchTerm, setSponsorSearchTerm] = useState('');
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: 'Alert',
    message: '',
    onClose: null
  });

  const getMongoId = (entity) => {
    if (!entity) return '';
    if (entity._id) return entity._id;
    if (typeof entity.id === 'string' && entity.id.length === 24) return entity.id;
    return '';
  };

  const selectedAssociateId = getMongoId(transferFormData.associate);

  const fetchAssociates = async (search = '') => {
    setAssociatesLoading(true);
    try {
      const result = await getAssociates({ search, limit: 10 });
      if (result.status) {
        setAssociates(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to fetch associates');
      }
    } catch (err) {
      console.error('Error fetching associates:', err);
      setAssociates([]);
    } finally {
      setAssociatesLoading(false);
    }
  };

  const fetchSponsors = async (search = '', transferForAssociateId = '') => {
    if (!transferForAssociateId) {
      setSponsors([]);
      setSponsorsLoading(false);
      return;
    }

    setSponsorsLoading(true);
    try {
      const result = await getAssociates({
        search,
        limit: 10,
        transferForAssociateId,
      });
      if (result.status) {
        setSponsors(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to fetch sponsors');
      }
    } catch (err) {
      console.error('Error fetching sponsors:', err);
      setSponsors([]);
    } finally {
      setSponsorsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssociates();
  }, []);

  // Debounced search for associates
  useEffect(() => {
    const timer = setTimeout(() => {
      if (associateSearchTerm) {
        fetchAssociates(associateSearchTerm);
      } else {
        fetchAssociates();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [associateSearchTerm]);

  // Debounced search for sponsors
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSponsors(sponsorSearchTerm, selectedAssociateId);
    }, 500);
    return () => clearTimeout(timer);
  }, [selectedAssociateId, sponsorSearchTerm]);

  const associateDirectory = useMemo(() => ([
    {
      id: 'ASS001',
      name: 'John Smith',
      sponsorId: 'SP101',
      sponsorName: 'Sarah Johnson',
      sponsorCommissionPercent: '12.00',
      associateCommissionPercent: '5.00',
      teamMembers: [
        { id: 'TM001', name: 'Alex Green', role: 'Executive', designation: 'Field Associate' },
        { id: 'TM002', name: 'Priya Patel', role: 'Executive', designation: 'Inside Sales' },
      ],
    },
    {
      id: 'ASS002',
      name: 'Emma Wilson',
      sponsorId: 'SP109',
      sponsorName: 'David Brown',
      sponsorCommissionPercent: '11.00',
      associateCommissionPercent: '4.50',
      teamMembers: [
        { id: 'TM003', name: 'Rahul Verma', role: 'Coordinator', designation: 'Team Lead' },
        { id: 'TM004', name: 'Neha Sharma', role: 'Executive', designation: 'Field Associate' },
      ],
    },
    {
      id: 'ASS003',
      name: 'Robert Taylor',
      sponsorId: 'SP118',
      sponsorName: 'Anna Garcia',
      sponsorCommissionPercent: '10.50',
      associateCommissionPercent: '4.00',
      teamMembers: [
        { id: 'TM005', name: 'Shivani Rao', role: 'Executive', designation: 'Inside Sales' },
      ],
    },
    {
      id: 'ASS004',
      name: 'Maria Rodriguez',
      sponsorId: 'SP201',
      sponsorName: 'James Lee',
      sponsorCommissionPercent: '13.00',
      associateCommissionPercent: '6.00',
      teamMembers: [],
    },
  ]), []);

  const sponsorDirectory = useMemo(() => ([
    {
      id: 'SP301',
      name: 'Mike Davis',
      sponsorCommissionPercent: '11.50',
      teamMembers: [
        { id: 'SPTM001', name: 'Ravi Kumar', role: 'Coordinator', designation: 'Sales Lead' },
      ],
    },
    {
      id: 'SP302',
      name: 'Lisa Chen',
      sponsorCommissionPercent: '12.50',
      teamMembers: [
        { id: 'SPTM002', name: 'Sameer Joshi', role: 'Executive', designation: 'Field Associate' },
        { id: 'SPTM003', name: 'Anita Desai', role: 'Executive', designation: 'Inside Sales' },
      ],
    },
    {
      id: 'SP303',
      name: 'Tom Wilson',
      sponsorCommissionPercent: '10.00',
      teamMembers: [],
    },
    {
      id: 'SP304',
      name: 'Karen White',
      sponsorCommissionPercent: '9.50',
      teamMembers: [
        { id: 'SPTM004', name: 'Harish Iyer', role: 'Executive', designation: 'Field Associate' },
      ],
    },
  ]), []);

  const handleGoBack = () => {
    history.goBack();
  };

  const handleRefresh = () => {
    setAssociateSearchTerm('');
    setSponsorSearchTerm('');
    setTransferFormData({
      transferDate: new Date().toISOString().slice(0, 16),
      associate: null,
      toSponsor: null,
      remarks: '',
    });
    fetchAssociates();
  };

  const showAlert = (message, title = 'Alert', onClose = null) => {
    setAlertModal({ open: true, title, message, onClose });
  };

  const handleCloseAlert = () => {
    const { onClose } = alertModal;
    setAlertModal((prev) => ({ ...prev, open: false }));
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  const handleTransferSubmit = async () => {
    if (!transferFormData.associate || !transferFormData.toSponsor) {
      showAlert('Please select an associate and target sponsor.', 'Warning');
      return;
    }

    setTransferLoading(true);

    try {
      const associateId = transferFormData.associate.id || transferFormData.associate._id || transferFormData.associate.associateId;
      const transferToSponsorId = transferFormData.toSponsor.id || transferFormData.toSponsor._id || transferFormData.toSponsor.associateId || transferFormData.toSponsor.sponsorId;

      const result = await transferAssociate({
        associateId,
        transferToSponsorId,
      });

      if (result.status) {
        showAlert(
          `Associate ${transferFormData.associate.name || transferFormData.associate.associateName} has been successfully transferred to ${transferFormData.toSponsor.name || transferFormData.toSponsor.associateName || transferFormData.toSponsor.sponsorName}`,
          'Success',
          () => history.goBack()
        );
      } else {
        throw new Error(result.message || 'Transfer failed');
      }
    } catch (err) {
      console.error('Error transferring associate:', err);

      // Handle detailed backend validation errors
      let errorMessage = 'Transfer failed';
      if (err.errors && Array.isArray(err.errors)) {
        // Show the first validation error message
        errorMessage = err.errors[0]?.msg || err.message || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }

      showAlert(errorMessage, 'Error');
    } finally {
      setTransferLoading(false);
    }
  };
  const handleShowList = () => {
    history.push('/app/user/AssociateChange');
  };

  const renderTeamTable = (rows, emptyLabel) => (
    <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <Table size="small">
        <TableHead sx={{ backgroundColor: '#f4f6fa' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>ID</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Role</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Designation</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows && rows.length ? (
            rows.map((member) => (
              <TableRow key={member.id} hover>
                <TableCell>{member.id}</TableCell>
                <TableCell>{member.name}</TableCell>
                <TableCell>{member.role}</TableCell>
                <TableCell>{member.designation}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                {emptyLabel}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Associate Transfer. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f9f9fb', minHeight: '100vh' }}>
      {/* Header with Back Button */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <IconButton onClick={handleGoBack} sx={{ color: '#536DFE' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
              Sponsor Transfer
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Transfer associate to a new sponsor
          </Typography>
        </Box>
        <RefreshButton onClick={handleRefresh} loading={associatesLoading} />
      </Box>

      <Paper sx={{ p: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#000' }}>
          Associate Transfer To Sponsor
        </Typography>

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={5} display="flex" flexDirection="column" gap={3}>
            <TextField
              fullWidth
              label="Transfer Date"
              type="datetime-local"
              value={transferFormData.transferDate}
              onChange={(e) => setTransferFormData((prev) => ({ ...prev, transferDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <Autocomplete
              fullWidth
              options={associates.filter(associate => {
                // Don't show the selected sponsor in associate options
                if (!transferFormData.toSponsor) return true;
                const associateId = associate.id || associate._id || associate.associateId;
                const sponsorId = transferFormData.toSponsor.id || transferFormData.toSponsor._id || transferFormData.toSponsor.associateId || transferFormData.toSponsor.sponsorId;
                return associateId !== sponsorId;
              })}
              loading={associatesLoading}
              getOptionLabel={(option) => {
                const name = option.name || option.associateName;
                const id = option.id || option._id || option.associateId;
                return name ? `${name} (${id})` : '';
              }}
              isOptionEqualToValue={(option, value) => {
                const optionId = option.id || option._id || option.associateId;
                const valueId = value?.id || value?._id || value?.associateId;
                return optionId === valueId;
              }}
              value={transferFormData.associate}
              inputValue={associateSearchTerm}
              onInputChange={(event, newValue) => {
                setAssociateSearchTerm(newValue);
              }}
              filterOptions={(options) => options} // Already handled by API
              onChange={(event, newValue) =>
                {
                  setTransferFormData((prev) => ({
                    ...prev,
                    associate: newValue,
                    toSponsor: null,
                  }));
                  setSponsorSearchTerm('');
                  setSponsors([]);
                }
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Associate"
                  placeholder="Select associate"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {associatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              )}
              renderOption={(props, option) => {
                const name = option.name || option.associateName;
                const id = option.id || option._id || option.associateId;
                return (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {id}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
            />

            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Selected Associate Detail:
              </Typography>
              {transferFormData.associate ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">ID</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {transferFormData.associate.associateId || transferFormData.associate.id || transferFormData.associate._id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {transferFormData.associate.name || transferFormData.associate.associateName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Sponsor By Id</Typography>
                    <Typography variant="body2">
                      {transferFormData.associate.sponsorInfo?.sponsorId || transferFormData.associate.sponsorBy || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Sponsor By Name</Typography>
                    <Typography variant="body2">
                      {transferFormData.associate.sponsorInfo?.sponsorName || transferFormData.associate.sponsorName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Associate Commission %</Typography>
                    <Typography variant="body2">
                      {transferFormData.associate.commission || transferFormData.associate.associateCommissionPercent || 'N/A'} %
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Transfer To Sponsor</Typography>
                    <Typography variant="body2">
                      {transferFormData.toSponsor
                        ? `${transferFormData.toSponsor.name || transferFormData.toSponsor.sponsorName} (${transferFormData.toSponsor.id || transferFormData.toSponsor._id})`
                        : 'Not selected yet'}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Please select an associate to view details.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Associate Team Detail:
              </Typography>
              {renderTeamTable(
                transferFormData.associate?.teamMembers || transferFormData.associate?.team || [],
                'No team members found for selected associate.'
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={2} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
            <IconButton
              disableRipple
              sx={{
                bgcolor: '#536DFE',
                color: '#fff',
                width: 56,
                height: 56,
                '&:hover': {
                  bgcolor: '#4558D6',
                },
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
            {canWrite && (
              <Button
                fullWidth
                variant="contained"
                onClick={handleTransferSubmit}
                disabled={!transferFormData.associate || !transferFormData.toSponsor || transferLoading}
                sx={{
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: '0 6px 20px rgba(83, 109, 254, 0.25)',
                }}
              >
                {transferLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Transferring...
                  </>
                ) : (
                  'Transfer'
                )}
              </Button>
            )}
          </Grid>

          <Grid item xs={12} md={5} display="flex" flexDirection="column" gap={3}>
            <Box sx={{ visibility: 'hidden', height: 0 }} />
            <Autocomplete
              fullWidth
              disabled={!selectedAssociateId}
              options={sponsors.filter(sponsor => {
                // Don't show the selected associate in sponsor options
                if (!transferFormData.associate) return true;
                const sponsorId = sponsor.id || sponsor._id || sponsor.associateId || sponsor.sponsorId;
                const associateId = transferFormData.associate.id || transferFormData.associate._id || transferFormData.associate.associateId;
                return sponsorId !== associateId;
              })}
              loading={sponsorsLoading}
              getOptionLabel={(option) => {
                const name = option.name || option.associateName || option.sponsorName;
                const id = option.id || option._id || option.associateId || option.sponsorId;
                return name ? `${name} (${id})` : '';
              }}
              isOptionEqualToValue={(option, value) => {
                const optionId = option.id || option._id || option.associateId || option.sponsorId;
                const valueId = value?.id || value?._id || value?.associateId || value?.sponsorId;
                return optionId === valueId;
              }}
              value={transferFormData.toSponsor}
              inputValue={sponsorSearchTerm}
              onInputChange={(event, newValue) => {
                setSponsorSearchTerm(newValue);
              }}
              filterOptions={(options) => options} // Already handled by API
              onChange={(event, newValue) => setTransferFormData((prev) => ({ ...prev, toSponsor: newValue }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="To Sponsor"
                  placeholder={selectedAssociateId ? 'Select new sponsor' : 'Select associate first'}
                  helperText={selectedAssociateId ? '' : 'Select associate first to load eligible sponsors'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {sponsorsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              )}
              renderOption={(props, option) => {
                const name = option.name || option.associateName || option.sponsorName;
                const id = option.id || option._id || option.associateId || option.sponsorId;
                return (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {id}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
            />

            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Selected Sponsor Detail:
              </Typography>
              {transferFormData.toSponsor ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Sponsor By Id</Typography>
                    <Typography variant="body2">
                      {transferFormData.toSponsor.associateId || transferFormData.toSponsor.id || transferFormData.toSponsor._id || transferFormData.toSponsor.sponsorId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Sponsor By Name</Typography>
                    <Typography variant="body2">
                      {transferFormData.toSponsor.name || transferFormData.toSponsor.associateName || transferFormData.toSponsor.sponsorName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">To Sponsor Commission %</Typography>
                    <Typography variant="body2">
                      {transferFormData.toSponsor.commission || transferFormData.toSponsor.sponsorCommissionPercent || 'N/A'} %
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Please select a sponsor to view details.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Associate Team Detail:
              </Typography>
              {renderTeamTable(
                transferFormData.toSponsor?.teamMembers || transferFormData.toSponsor?.team || [],
                'No team members found for selected sponsor.'
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Remark"
              multiline
              rows={3}
              value={transferFormData.remarks}
              onChange={(e) => setTransferFormData((prev) => ({ ...prev, remarks: e.target.value }))}
              placeholder="Enter transfer remarks (optional)"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleShowList}
                sx={{
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Show List
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={handleCloseAlert}
      />
    </Box>
  );
};

export default AssociateTransfer;
